import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, Loader2, Volume2, ShieldAlert, BookMarked, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, speak } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WordIcon } from "@/components/WordIcon";
import { useTranslations, Translation } from "@/lib/translations";

export default function PhotoIdentifyPage() {
  const { user } = useAuth();
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [added, setAdded] = useState(false);
  const { map, rtl } = useTranslations(res?.found ? [res] : []);

  const pick = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setRes(null);
    setAdded(false);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/photo-identify", form);
      setRes(data);
      if (data.found) speak(data.word, 0.9);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addToLexicon = async () => {
    try {
      await api.post("/teams/lexicon", { word: res.word, definition: res.definition, example: res.example || "" });
      setAdded(true);
      toast.success("Ajouté au lexique de l'équipe");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="photo-identify-page">
      <Link to="/vocabulaire" data-testid="photo-back-btn" className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-5 h-5" /> Mes mots
      </Link>
      <h1 className="font-display text-4xl font-extrabold uppercase">C'est quoi cet outil ?</h1>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="photo-identify-input" />
      {preview && <img src={preview} alt="Photo prise" className="w-full max-h-72 object-cover rounded-2xl border-4 border-slate-900" data-testid="photo-identify-preview" />}
      <button
        onClick={() => ref.current?.click()}
        disabled={busy}
        data-testid="photo-identify-btn"
        className="w-full h-16 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display text-xl font-bold uppercase flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : preview ? <RotateCw className="w-6 h-6" /> : <Camera className="w-6 h-6" />}
        {busy ? "Je regarde…" : preview ? "Autre photo" : "Prendre une photo"}
      </button>

      {res && !res.found && (
        <p className="rounded-xl bg-amber-100 text-amber-900 p-4 font-semibold" data-testid="photo-identify-notfound">Je ne reconnais pas d'outil de chantier. Essayez de cadrer l'objet de plus près.</p>
      )}
      {res?.found && (
        <div className="bg-white rounded-2xl border-2 border-slate-200 border-l-8 border-l-orange-500 p-5 space-y-3 rise" data-testid="photo-identify-result">
          <div className="flex items-center gap-3">
            <WordIcon word={res} />
            <div className="flex-1">
              <p className="font-display text-3xl font-bold" data-testid="photo-identify-word">{res.word}</p>
              <Translation text={map[res.word]} rtl={rtl} testId="photo-identify-translation" />
            </div>
            <button onClick={() => speak(`${res.word}. ${res.example || ""}`, 0.9)} aria-label="Écouter" data-testid="photo-identify-audio-btn" className="w-12 h-12 rounded-full bg-slate-900 text-amber-400 hover:bg-orange-500 hover:text-white grid place-items-center transition-colors">
              <Volume2 className="w-6 h-6" />
            </button>
          </div>
          <p className="text-slate-700" data-testid="photo-identify-definition">{res.definition}</p>
          {res.usage && <p className="text-sm"><b>À quoi ça sert :</b> {res.usage}</p>}
          {res.example && <p className="text-sm italic text-slate-600 bg-slate-50 rounded-lg px-3 py-2">« {res.example} »</p>}
          {res.safety && (
            <p className="flex items-start gap-2 text-sm font-semibold text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="photo-identify-safety">
              <ShieldAlert className="w-5 h-5 shrink-0" /> {res.safety}
            </p>
          )}
          {user.team_id && (
            <button onClick={addToLexicon} disabled={added} data-testid="photo-identify-add-lexicon-btn" className="w-full h-12 rounded-xl border-2 border-slate-900 font-bold flex items-center justify-center gap-2 hover:bg-slate-900 hover:text-white transition-colors disabled:opacity-50">
              <BookMarked className="w-5 h-5" /> {added ? "Ajouté au lexique" : "Ajouter au lexique de l'équipe"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
